import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import type { ReactNode } from 'react';

import type { Action } from '../../../common/types';
import { envType } from '../../../common/types';
import { getEnvType } from '../../../utils';
import {
	ADMIN_HUB_BASE_URL_PROD,
	ADMIN_HUB_BASE_URL_STG,
	GITHUB_MANAGE_LINK_PROD,
	GITHUB_MANAGE_LINK_STG,
} from '../../../utils/constants';
import { BillingIcon, UsersIcon } from '../common/icons';

export type ManagementTool = {
	title: string;
	description: string;
	icon: ReactNode;
	primaryAction: Action;
	secondaryAction?: Action;
	testId?: string;
};

const getAdminHubLink = (orgId: string, siteId: string) => {
	const currentEnv = getEnvType();
	const baseUrl = currentEnv === envType.PROD ? ADMIN_HUB_BASE_URL_PROD : ADMIN_HUB_BASE_URL_STG;

	return `${baseUrl}/o/${orgId}/products/devai/${siteId}`;
};

const getSubscriptionsLink = (orgId: string, siteId: string, devAiEntitlementID: string) => {
	const currentEnv = getEnvType();
	const baseUrl = currentEnv === envType.PROD ? ADMIN_HUB_BASE_URL_PROD : ADMIN_HUB_BASE_URL_STG;

	return `${baseUrl}/billing/o/${orgId}/${siteId}/entitlement/${devAiEntitlementID}`;
};

export const getGithubLink = () => {
	const currentEnv = getEnvType();

	if (currentEnv === envType.LOCAL) {
		return process.env.REMOTE_DOMAIN + GITHUB_MANAGE_LINK_STG;
	}

	const baseGithubIntegrationUrl = `${window.location.protocol}//${window.location.host}`;

	return currentEnv === envType.PROD
		? `${baseGithubIntegrationUrl}${GITHUB_MANAGE_LINK_PROD}`
		: `${baseGithubIntegrationUrl}${GITHUB_MANAGE_LINK_STG}`;
};

export const getManagementTools = (
	orgId: string,
	siteId: string,
	devAiEntitlementID: string | null,
): ManagementTool[] => {
	return [
		{
			title: 'Users',
			description: 'Add or remove users and administrators for Rovo Dev Agents.',
			icon: <UsersIcon />,
			primaryAction: {
				label: 'Manage user access',
				icon: LinkExternalIcon,
				href: { link: getAdminHubLink(orgId, siteId) },
				analyticEvent: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'manage-users',
					source: 'home-page-manage',
				},
			},
			testId: 'manage-users',
		},
		{
			title: 'Billing',
			description: 'View and manage your subscription status for Rovo Dev Agents.',
			icon: <BillingIcon />,
			primaryAction: {
				label: 'Manage subscription',
				icon: LinkExternalIcon,
				href: {
					link:
						devAiEntitlementID !== null
							? getSubscriptionsLink(orgId, siteId, devAiEntitlementID)
							: '',
				},
				analyticEvent: {
					action: 'clicked',
					actionSubject: 'link',
					actionSubjectId: 'manage-subscription',
					source: 'home-page-manage',
				},
			},
			testId: 'manage-subscription',
		},
	];
};
